import { useMediaQuery } from "Shared/Hooks/useMediaQuery";
import { useEffect, useState } from "react";

/**
 * The orientation of a calculator module's advisor (editing) layer.
 *
 * - `Row` — inputs stacked above the results.
 * - `Column` — inputs in a left rail, results on the right (default on wide
 *   screens; mirrors the prototype).
 *
 * `as const` object so call sites reference `ModuleLayoutOrientation.Column`
 * instead of bare string literals — same pattern as the API-derived enums.
 */
export const ModuleLayoutOrientation = {
	Row: "row",
	Column: "column",
} as const;
export type ModuleLayoutOrientation =
	(typeof ModuleLayoutOrientation)[keyof typeof ModuleLayoutOrientation];

const STORAGE_KEY = "modelingstudio:module-layout";
const CHANGE_EVENT = "modelingstudio:module-layout-change";

/**
 * The column layout needs a genuine two-pane width, so it's only offered from
 * this breakpoint up (≈ tablet landscape / laptop). Below it the layout is
 * always Row and the toggle is hidden. Kept in sync with the `lg:` breakpoint
 * used by <ModuleShell />'s column markup.
 */
const COLUMN_MIN_WIDTH = "(min-width: 1024px)";

const isOrientation = (value: unknown): value is ModuleLayoutOrientation =>
	value === ModuleLayoutOrientation.Row ||
	value === ModuleLayoutOrientation.Column;

const readStored = (): ModuleLayoutOrientation => {
	if (typeof window === "undefined") return ModuleLayoutOrientation.Column;
	const raw = window.localStorage.getItem(STORAGE_KEY);
	// Default to the column (two-pane) layout, like the prototype.
	return isOrientation(raw) ? raw : ModuleLayoutOrientation.Column;
};

/**
 * Module layout orientation (row / column) with localStorage persistence and
 * cross-instance sync via a custom event, so the TopBar toggle and every
 * <ModuleShell /> stay in sync without prop drilling. Mirrors {@link useTheme}.
 *
 * `orientation` is the EFFECTIVE orientation: it collapses to `Row` below the
 * column breakpoint regardless of the stored preference. `canUseColumn` reports
 * whether the viewport is wide enough to offer the column layout (used to hide
 * the toggle on small screens).
 */
export const useModuleLayout = (): {
	orientation: ModuleLayoutOrientation;
	setOrientation: (next: ModuleLayoutOrientation) => void;
	canUseColumn: boolean;
} => {
	const [stored, setStoredState] =
		useState<ModuleLayoutOrientation>(readStored);
	const canUseColumn = useMediaQuery(COLUMN_MIN_WIDTH);

	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<ModuleLayoutOrientation>).detail;
			if (!isOrientation(detail)) return;
			setStoredState((prev) => (prev === detail ? prev : detail));
		};
		window.addEventListener(CHANGE_EVENT, handler);
		return () => window.removeEventListener(CHANGE_EVENT, handler);
	}, []);

	const setOrientation = (next: ModuleLayoutOrientation) => {
		window.localStorage.setItem(STORAGE_KEY, next);
		setStoredState(next);
		window.dispatchEvent(
			new CustomEvent<ModuleLayoutOrientation>(CHANGE_EVENT, { detail: next }),
		);
	};

	const orientation =
		stored === ModuleLayoutOrientation.Column && canUseColumn
			? ModuleLayoutOrientation.Column
			: ModuleLayoutOrientation.Row;

	return { orientation, setOrientation, canUseColumn };
};
